// copied and adapted from Willy

import PropTypes from "prop-types";
import React from "react";

import { useTranslation, withTranslation } from "react-i18next";
import EventConfigCard from "./components/event-config-card";
import LineTable from "../../../components/generic/lineTable";
import Loader from "../../../components/ui/loader";
import SortButton from "../../../components/generic/sortButton";
import {
	FilterSetController,
	Icon,
	WeezHeader,
	EmptyState,
	Card
} from "@weezevent/nacre";

import { useHistory } from "react-router-dom";
import css from "./event-config-list.module.css";
import useEventList from "../../../api/api-hooks/useEventList";
import useEventStats from "../../../api/api-hooks/useEventStats";
import MainCtaCreateButton from "../../../components/event/mainCtaCreateButton";
import useOrganizationContext from "hooks/useOrganizationContext";

/**
 * Event list selection page
 * @param {*} param0
 */
export function EventConfigList({ orgaPath }) {
	const { t } = useTranslation();
	const organization = useOrganizationContext();
	const organizationId = organization.id();
	React.useEffect(() => {
		document.title = ` O${organizationId} • ${t(
			"access.titles.events-screen"
		)}`;
	}, [organizationId, t]);

	const history = useHistory();

	const initial_sort = "-event_date";
	const [sortValue, setSortValue] = React.useState(initial_sort);

	let [inputSearchValue, setInputSearchValue] = React.useState("");
	const [sortToggle, setSortToggle] = React.useState(false);

	const sortOptions = React.useMemo(() => {
		const options = [
			{
				key: "name",
				label: t("access.event.list.sort.name-up"),
				value: "name"
			},
			{
				key: "-name",
				label: t("access.event.list.sort.name-down"),
				value: "-name"
			},
			{
				key: "event_date",
				label: t("access.event.list.sort.ticket-event_date-up"),
				value: "event_date"
			},
			{
				key: "-event_date",
				label: t("access.event.list.sort.ticket-event_date-down"),
				value: "-event_date"
			}
		];
		return options;
	}, [t]);

	const { data } = useEventList({
		params: {
			with_config: false
		}
	});

	const { data: events, isLoading: loadingEvents } = useEventList({
		params: {
			with_config: true,
			expand: ["event_config"],
			search: inputSearchValue,
			ordering: sortValue
		}
	});

	const {
		data: stats,
		isLoading: loadingStats,
		isSuccess: statsSuccess
	} = useEventStats({
		params: {
			with_config: true,
			search: inputSearchValue
		}
	});

	const eventsWithStats = React.useMemo(() => {
		if (!events) return [];
		if (!statsSuccess) return events;

		const eventsStats = Object.fromEntries(
			(stats || []).map(stat => [stat.id, stat.attendee_count ?? 0])
		);

		return events.map(ev => ({
			...ev,
			stats: { attendee_count: eventsStats[ev.id] || 0 }
		}));
	}, [events, stats, statsSuccess]);

	const countEvents = React.useMemo(() => {
		return events?.length || 0;
	}, [events]);

	let handleSearch = React.useCallback((synth, value) => {
		setInputSearchValue(value);
	}, []);

	const handleSort = React.useCallback(
		item => {
			setSortValue(item);
		},
		[setSortValue]
	);

	const handleActiveSort = React.useCallback(() => {
		setSortToggle(!sortToggle);
	}, [sortToggle]);

	return (
		<>
			<WeezHeader
				title={t("access.event.list.header.title")}
				rightComponent={[
					<MainCtaCreateButton
						key={"add"}
						label={t("access.event.list.header.cta.add")}
						onlyIconMobile={true}
						onClick={() => history.push(`${orgaPath}/events/new`)}
						disabled={data?.length === 0}
					/>
				]}
			>
				{t("access.event.list.header.subtitle")}
			</WeezHeader>

			<FilterSetController
				className={css["filter-block"]}
				total={String(countEvents)}
				labelTotal={t("access.event.list.filterset.count_event", {
					count: countEvents
				})}
				labelFilters={t("filter")}
				labelReset={t("ticket.common.cta.reset")}
				onChange={() => {}}
				search={{
					onChange: (synth, value) => {
						handleSearch && handleSearch(synth, value);
					},
					handleReset: () => {
						handleSearch("");
					},
					placeholder: t(
						"access.event.list.filterset.search.placeholder"
					),
					value: inputSearchValue
				}}
				leftComponent={
					<SortButton
						active={sortValue !== initial_sort}
						sortValue={sortValue}
						sort={handleSort}
						onChange={handleActiveSort}
						options={sortOptions}
						label={t("access.event.list.sort.button.label")}
					/>
				}
			/>

			{loadingEvents ? (
				<Loader active />
			) : events?.length > 0 ? (
				<>
					<LineTable>
						{eventsWithStats.map((event, index) => (
							<EventConfigCard
								key={index}
								event={event}
								organization={organization}
								loadingStats={loadingStats}
							/>
						))}
					</LineTable>
				</>
			) : (
				<Card>
					<Card.Content>
						<EmptyState
							icon={<Icon name="list" color="access" />}
							title={t("access.event.list.empty_state.title")}
							subtitle={t(
								"access.event.list.empty_state.subtitle"
							)}
						/>
					</Card.Content>
				</Card>
			)}
		</>
	);
}

EventConfigList.propTypes = {
	orgaPath: PropTypes.string.isRequired
};

EventConfigList.defaultProps = {
	events: []
};

export default withTranslation()(EventConfigList);
